/**
 * Unicode-safe download names shared by the Excel export and the deck PDF.
 *
 * Names are normalized to NFC, then letters, digits, and combining marks stay
 * (ñ, ó, decomposed accents, Hindi vowel signs). Everything else collapses to
 * a single underscore. Lone surrogates are dropped before normalization
 * because `String.prototype.normalize` and `encodeURIComponent` throw on them.
 *
 * The Content-Disposition helper speaks RFC 6266/5987: an ASCII `filename`
 * fallback plus `filename*=UTF-8''…`.
 *
 * One-shot pure functions. Not an LLM prompt.
 */

// Built from a string: the frontend typechecks this file with an ES5 target,
// which rejects the `u` flag on a regex literal. The runtime regex is the same.
const NON_NAME_CHARS = new RegExp('[^\\p{L}\\p{N}\\p{M}]+', 'gu');
const HEADER_UNSAFE = /[\r\n"\\/;]/g;

/** Drop unpaired UTF-16 surrogates. Valid pairs (emoji) stay. */
function stripLoneSurrogates(value: string): string {
  let out = '';
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        out += value[index] + value[index + 1];
        index += 1;
      }
      continue;
    }
    if (code >= 0xdc00 && code <= 0xdfff) continue;
    out += value[index];
  }
  return out;
}

function prepareFilenameText(value: string): string {
  return stripLoneSurrogates(value).normalize('NFC');
}

/** Stem with Unicode letters, digits, and combining marks kept. Empty when nothing remains. */
export function unicodeFilenameStem(name: string | null | undefined): string {
  return prepareFilenameText(name ?? '')
    .replace(HEADER_UNSAFE, '')
    .replace(NON_NAME_CHARS, '_')
    .replace(/^_+|_+$/g, '');
}

export function xlsxExportFilename(studyName: string | null | undefined): string {
  const stem = unicodeFilenameStem(studyName);
  return stem ? `${stem}_export.xlsx` : 'response_export.xlsx';
}

export function csvExportFilename(studyName: string | null | undefined): string {
  const stem = unicodeFilenameStem(studyName);
  return stem ? `${stem}_export.csv` : 'response_export.csv';
}

export function deckPdfFilename(studyName: string | null | undefined): string {
  const stem = unicodeFilenameStem(studyName);
  return stem ? `${stem}_deck.pdf` : 'deck.pdf';
}

export function deckPptxFilename(studyName: string | null | undefined): string {
  const stem = unicodeFilenameStem(studyName);
  return stem ? `${stem}_deck.pptx` : 'deck.pptx';
}

function encodeRfc5987(value: string): string {
  return encodeURIComponent(value).replace(
    /['()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

/**
 * `attachment` header. `filename` is ASCII (non-ASCII bytes become `_`).
 * `filename*` carries the UTF-8 name. CR/LF/quotes/slashes/semicolons are stripped
 * so the header cannot be split.
 */
export function attachmentContentDisposition(filename: string): string {
  const safe =
    prepareFilenameText(filename)
      .replace(HEADER_UNSAFE, '')
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .trim() || 'download';
  const ascii = safe.replace(/[^\x20-\x7E]/g, '_') || 'download';
  return `attachment; filename="${ascii}"; filename*=UTF-8''${encodeRfc5987(safe)}`;
}
